import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  createStyles,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Theme,
  Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { addDays } from 'date-fns';
import DatePicker from 'components/@common/Inputs/DatePicker';
import { ActivationRangeOptions } from 'types/enums';

interface ActivateMastercodeFormProps {
  nowDate: Date;
  minDate?: object | string | number | Date | null;
  maxDate?: object | string | number | Date | null;
  isValidDate: boolean;
  activationRangeValue: string;
  activationRangeDate: Date | null;
  warningText?: React.ReactNode;
  handleActivationChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateActivationChange: (date: Date | null) => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    subtitleHighlight: {
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2)
    },
    subtitle: {
      display: 'inline',
      fontWeight: theme.typography.fontWeightRegular
    },
    inputField: {
      marginBottom: theme.spacing(2)
    },
    radioText: {
      fontWeight: 500
    },
    radioContainer: {
      marginTop: theme.spacing(2.5)
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'row',
      marginLeft: theme.spacing(1)
    },
    radioFormControlLabel: {
      ...theme.typography.subtitle1,
      marginLeft: 0
    },
    dateWarning: { display: 'inline-flex' },
    dateWarningIcon: {
      color: theme.palette.grey[700],
      fontSize: theme.typography.pxToRem(12),
      paddingTop: theme.spacing(0.7)
    },
    dateWarningText: { marginLeft: theme.spacing(1) }
  });
});

const ActivateMastercodeForm: FC<ActivateMastercodeFormProps> = ({
  minDate,
  maxDate,
  nowDate,
  isValidDate,
  activationRangeValue,
  activationRangeDate,
  warningText,
  handleActivationChange,
  handleDateActivationChange
}) => {
  const classes = useStyles();
  const { t } = useTranslation('Order');

  return (
    <Container maxWidth="sm">
      <Grid item xs={12} sm={12}>
        <div className={classes.radioContainer}>
          <Typography className={classes.radioText}>{t('Activate Licenses')}</Typography>
          <RadioGroup
            defaultValue={ActivationRangeOptions.NOW}
            value={activationRangeValue}
            aria-label={t('activation date')}
            name="assignDateActivation"
            className={classes.radioGroup}
            onChange={handleActivationChange}
          >
            <FormControlLabel
              value={ActivationRangeOptions.NOW}
              control={<Radio />}
              label={t('Now')}
              classes={{ label: classes.radioFormControlLabel }}
            />
            <FormControlLabel
              value={ActivationRangeOptions.LATER}
              control={<Radio />}
              label={t('At a later time')}
              classes={{ label: classes.radioFormControlLabel }}
            />
          </RadioGroup>
        </div>
        {activationRangeValue === ActivationRangeOptions.LATER && (
          <Fragment>
            <DatePicker
              id="activation-date-picker"
              defaultValue={addDays(nowDate, 1)}
              className={classes.inputField}
              minDate={minDate}
              maxDate={maxDate}
              KeyboardButtonProps={{
                'aria-label': t('Activation date')
              }}
              inputProps={{
                'aria-label': t('Activation date')
              }}
              label={t('Select an Activation Date for your licenses')}
              onChange={handleDateActivationChange}
              value={activationRangeDate}
            />
            {warningText && activationRangeDate && isValidDate && (
              <Alert severity="info">{warningText}</Alert>
            )}
          </Fragment>
        )}
      </Grid>
    </Container>
  );
};

export default ActivateMastercodeForm;
